import { useState, type ReactNode } from "react";
import { Menu, MenuButton, MenuItems } from "@headlessui/react";
import { Button, Segmented } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import type { ThemePref } from "@/shared/theme/useTheme";

type Props = {
  storeName: string;
  storeShort: string | null;
  storeCode: string;
  /** Signed-in user's display name (footer row). */
  sellerName: string;
  /** Signed-in user's email (footer row). */
  sellerEmail: string;
  /** Single-letter avatar initial. */
  initial: string;
  /** Active locale ("en" | "ar"). */
  locale: string;
  /** Called when the user picks a language. */
  onLocaleChange: (next: "en" | "ar") => void;
  /** Whether the effective theme is dark (drives the sun/moon toggle). */
  isDark: boolean;
  /** Called when the user flips the theme. */
  onThemeChange: (next: ThemePref) => void;
  /** Open the personal info modal. */
  onPersonalInfo: () => void;
  /** Open the password change modal. */
  onPassword: () => void;
  /** Logout action — already wired to the proper redirect. */
  onLogout: () => void;
  /** Localized labels passed in so the component stays presentation-only. */
  labels: {
    storeMenu: string;
    live: string;
    copyCode: string;
    codeCopied: string;
    language: string;
    theme: string;
    themeLight: string;
    themeDark: string;
    themeSystem: string;
    personalInfo: string;
    password: string;
    logout: string;
  };
};

/**
 * Account menu — lives in the sidebar footer. The trigger is the signed-in
 * user's row (avatar + name + email + caret); clicking it opens a popover
 * (above the row) with the store header (avatar + name + code + Live + copy),
 * Language and Theme toggles, personal info / password rows, and Log out.
 * Presentation-only — all handlers/labels are injected by AppShell.
 */
export function AccountMenu({
  storeName,
  storeShort,
  storeCode,
  sellerName,
  sellerEmail,
  initial,
  locale,
  onLocaleChange,
  isDark,
  onThemeChange,
  onPersonalInfo,
  onPassword,
  onLogout,
  labels,
}: Props) {
  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <MenuButton
            aria-label={labels.storeMenu}
            className={[
              "flex w-full items-center gap-2.5 rounded-[10px] p-2 text-start transition-colors focus:outline-none",
              open
                ? "bg-[color:var(--sidebar-hover-bg)]"
                : "hover:bg-[color:var(--sidebar-hover-bg)]",
            ].join(" ")}
          >
            <span
              aria-hidden="true"
              className="flex h-[33px] w-[33px] shrink-0 items-center justify-center rounded-full bg-[color:var(--gold-500)] text-[13px] font-extrabold text-[#1A1A19]"
            >
              {initial}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-semibold leading-tight text-[color:var(--sidebar-ink)]">
                {sellerName}
              </span>
              <span className="block truncate text-[11px] text-[color:var(--sidebar-ink-disabled)]">
                {sellerEmail}
              </span>
            </span>
            <ChevronToggleIcon open={open} />
          </MenuButton>
          <MenuItems
            anchor="top start"
            className="z-50 mb-2 w-[296px] origin-bottom overflow-hidden rounded-[14px] border border-[color:var(--border-light)] bg-[color:var(--surface)] shadow-[0_24px_60px_rgba(15,20,40,0.16),0_4px_12px_rgba(15,20,40,0.06)] focus:outline-none"
          >
            <StoreHeader
              storeName={storeName}
              storeShort={storeShort}
              storeCode={storeCode}
              initial={initial}
              labels={labels}
            />
            <Section>
              <Row icon={<GlobeIcon />} label={labels.language}>
                <Segmented
                  tone="light"
                  value={locale}
                  onChange={(next) => onLocaleChange(next as "en" | "ar")}
                  options={[
                    { value: "en", label: "EN" },
                    { value: "ar", label: "ع" },
                  ]}
                  ariaLabel={labels.language}
                />
              </Row>
              <Row icon={isDark ? <MoonIcon /> : <SunIcon />} label={labels.theme}>
                <ThemeToggle
                  isDark={isDark}
                  onChange={onThemeChange}
                  lightLabel={labels.themeLight}
                  darkLabel={labels.themeDark}
                />
              </Row>
            </Section>
            <Divider />
            <Section>
              <Row
                icon={<UserIcon />}
                label={labels.personalInfo}
                onClick={onPersonalInfo}
              >
                <ChevronEndIcon className="text-[color:var(--text-muted)]" />
              </Row>
              <Row
                icon={<LockIcon />}
                label={labels.password}
                onClick={onPassword}
              >
                <ChevronEndIcon className="text-[color:var(--text-muted)]" />
              </Row>
            </Section>
            <Divider />
            <Section>
              <Row
                icon={<LogoutIcon />}
                label={labels.logout}
                onClick={onLogout}
                danger
              />
            </Section>
          </MenuItems>
        </>
      )}
    </Menu>
  );
}

function StoreHeader({
  storeName,
  storeShort,
  storeCode,
  initial,
  labels,
}: {
  storeName: string;
  storeShort: string | null;
  storeCode: string;
  initial: string;
  labels: Props["labels"];
}) {
  const [copied, setCopied] = useState(false);
  const t = useTranslations();
  const storeShortTooltip = t(keys.seller.storeShortTooltip);

  const handleCopy = async () => {
    if (await copyTextToClipboard(storeCode)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="flex items-center gap-2.5 border-b border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3.5 pt-3.5 pb-3">
      <div
        aria-hidden="true"
        className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[9px] text-[15px] font-bold text-white"
        style={{ background: "linear-gradient(135deg, #1f2c4d, #2f4170)" }}
      >
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] font-bold leading-tight text-[color:var(--text-primary)]">
          {storeName}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          {storeShort ? (
            <span
              className="font-mono text-[11px] uppercase tracking-[0.04em] text-[color:var(--text-muted)]"
              title={storeShortTooltip}
            >
              {storeShort}
            </span>
          ) : null}
          <span
            aria-hidden="true"
            className="inline-block h-[3px] w-[3px] rounded-full bg-[color:var(--text-muted)] opacity-60"
          />
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[color:var(--success-500)]">
            <span
              aria-hidden="true"
              className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--success-500)]"
            />
            {labels.live}
          </span>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void handleCopy()}
        aria-label={copied ? labels.codeCopied : labels.copyCode}
        title={copied ? labels.codeCopied : labels.copyCode}
        leadingIcon={copied ? <CheckIcon /> : <CopyIcon />}
        style={
          copied
            ? {
                background: "color-mix(in oklab, var(--success-500) 10%, white)",
                color: "var(--success-500)",
                borderColor: "color-mix(in oklab, var(--success-500) 25%, white)",
              }
            : undefined
        }
      />
    </div>
  );
}

function Section({ children }: { children: ReactNode }) {
  return <div className="p-1.5">{children}</div>;
}

function Divider() {
  return <div aria-hidden="true" className="h-px bg-[color:var(--border-light)]" />;
}

function Row({
  icon,
  label,
  hint,
  children,
  onClick,
  danger,
}: {
  icon: ReactNode;
  label: string;
  hint?: string;
  children?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
}) {
  const Wrapper: "button" | "div" = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={[
        "flex w-full items-center gap-2.5 rounded-[9px] px-3 py-2 text-start text-[13.5px] font-medium transition-colors",
        danger
          ? "text-[color:var(--error-500)] hover:bg-[color:var(--error-500)]/10"
          : "text-[color:var(--text-primary)] hover:bg-[color:var(--surface-alt)]",
        onClick ? "cursor-pointer" : "cursor-default",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        className={[
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px]",
          danger
            ? "bg-[color:var(--error-500)]/10 text-[color:var(--error-500)]"
            : "bg-[color:var(--surface-alt)] text-[color:var(--text-secondary)]",
        ].join(" ")}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block leading-tight">{label}</span>
        {hint ? (
          <span className="mt-0.5 block text-[11.5px] font-normal text-[color:var(--text-muted)]">
            {hint}
          </span>
        ) : null}
      </span>
      {children}
    </Wrapper>
  );
}

/* ── Icons ─────────────────────────────────────────────────────── */

const SVG_PROPS = {
  width: 15,
  height: 15,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function GlobeIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

/** Sun / moon segmented toggle — light vs dark (no system option). */
function ThemeToggle({
  isDark,
  onChange,
  lightLabel,
  darkLabel,
}: {
  isDark: boolean;
  onChange: (next: "light" | "dark") => void;
  lightLabel: string;
  darkLabel: string;
}) {
  const base =
    "flex h-7 w-7 items-center justify-center rounded-[7px] transition-colors";
  const active = "bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-[var(--shadow-sm)]";
  const idle = "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]";
  return (
    <div className="inline-flex items-center gap-0.5 rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-0.5">
      <button
        type="button"
        aria-label={lightLabel}
        aria-pressed={!isDark}
        onClick={() => onChange("light")}
        className={[base, isDark ? idle : active].join(" ")}
      >
        <SunIcon />
      </button>
      <button
        type="button"
        aria-label={darkLabel}
        aria-pressed={isDark}
        onClick={() => onChange("dark")}
        className={[base, isDark ? active : idle].join(" ")}
      >
        <MoonIcon />
      </button>
    </div>
  );
}

function UserIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg {...SVG_PROPS} width={14} height={14}>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...SVG_PROPS} width={14} height={14}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ChevronEndIcon({ className }: { className?: string }) {
  return (
    <svg
      {...SVG_PROPS}
      width={14}
      height={14}
      className={["rtl:rotate-180", className ?? ""].join(" ")}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/**
 * Up/down caret at the end of the footer user-row. The menu opens upward, so
 * the caret points up when closed and flips down when open.
 */
function ChevronToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={[
        "shrink-0 text-[color:var(--sidebar-ink-disabled)] transition-transform duration-150",
        open ? "rotate-180 text-[color:var(--sidebar-ink)]" : "",
      ].join(" ")}
    >
      <path d="m8 9 4-4 4 4" />
      <path d="m16 15-4 4-4-4" />
    </svg>
  );
}
